"use client";

/**
 * The UK, EU and US flags the research and Venus pages draw. The region studies stay on the
 * concept board (concept/groups/drawing-variant-regions.tsx).
 */

const EU_STAR_POSITIONS = [
  { x: 50, y: 16.33 },
  { x: 58.5, y: 18.61 },
  { x: 64.72, y: 24.83 },
  { x: 67, y: 33.33 },
  { x: 64.72, y: 41.83 },
  { x: 58.5, y: 48.05 },
  { x: 50, y: 50.33 },
  { x: 41.5, y: 48.05 },
  { x: 35.28, y: 41.83 },
  { x: 33, y: 33.33 },
  { x: 35.28, y: 24.83 },
  { x: 41.5, y: 18.61 },
] as const;

const EU_STAR_POINTS = "0,-2.8 0.82,-0.88 2.85,-0.88 1.18,0.36 1.76,2.4 0,1.2 -1.76,2.4 -1.18,0.36 -2.85,-0.88 -0.82,-0.88";

const US_STRIPES = Array.from({ length: 13 }, (_, index) => index);
const US_STAR_POINTS = Array.from({ length: 9 }, (_, row) => {
  const count = row % 2 === 0 ? 6 : 5;
  return Array.from({ length: count }, (_, column) => ({
    x: ((column + 1) / (count + 1)) * 40,
    y: ((row + 1) / 10) * (7 / 13) * 100,
  }));
}).flat();

type FlagProps = {
  x: number;
  y: number;
  width: number;
  height: number;
  opacity?: number;
};

export function EuFlag({ x, y, width, height, opacity = 1 }: FlagProps) {
  return (
    <g aria-hidden="true" opacity={opacity} transform={`translate(${x} ${y}) scale(${width / 100} ${height / 66.67})`}>
      <rect width="100" height="66.67" rx="1.5" fill="#003399" />
      {EU_STAR_POSITIONS.map((star, index) => (
        <polygon
          key={`eu-star-${index}`}
          points={EU_STAR_POINTS}
          fill="#FFCC00"
          transform={`translate(${star.x} ${star.y})`}
        />
      ))}
      <rect width="100" height="66.67" rx="1.5" fill="none" stroke="#001F5B" strokeWidth="1" />
    </g>
  );
}

export function UsFlag({ x, y, width, height, opacity = 1 }: FlagProps) {
  return (
    <g aria-hidden="true" opacity={opacity} transform={`translate(${x} ${y}) scale(${width / 100} ${height / 52.63})`}>
      <rect width="100" height="52.63" fill="#FFFFFF" />
      {US_STRIPES.map((stripe) =>
        stripe % 2 === 0 ? (
          <rect key={`us-stripe-${stripe}`} y={(stripe * 52.63) / 13} width="100" height={52.63 / 13} fill="#B22234" />
        ) : null,
      )}
      <rect width="40" height={(7 * 52.63) / 13} fill="#3C3B6E" />
      {US_STAR_POINTS.map((star, index) => (
        <circle key={`us-star-${index}`} cx={star.x} cy={(star.y * 52.63) / 100} r="0.9" fill="#FFFFFF" />
      ))}
      <rect width="100" height="52.63" fill="none" stroke="#6B1B26" strokeWidth="0.8" />
    </g>
  );
}

/**
 * Union Jack in the same simplified idiom as the EU and US marks: field,
 * diagonals, cross, hairline border. Meant to sit inside an <svg> whose
 * viewBox clips the diagonal stroke ends at the corners.
 */
export function UkFlag({ x, y, width, height, opacity = 1 }: FlagProps) {
  return (
    <g
      aria-hidden="true"
      opacity={opacity}
      transform={`translate(${x} ${y}) scale(${width / 100} ${height / 66.67})`}
    >
      <rect width="100" height="66.67" rx="1.5" fill="#012169" />
      <path d="M0 0 L100 66.67 M100 0 L0 66.67" stroke="#FFFFFF" strokeWidth="12" />
      <path d="M0 0 L100 66.67 M100 0 L0 66.67" stroke="#C8102E" strokeWidth="4.5" />
      <path d="M50 0 V66.67 M0 33.33 H100" stroke="#FFFFFF" strokeWidth="20" />
      <path d="M50 0 V66.67 M0 33.33 H100" stroke="#C8102E" strokeWidth="12" />
      <rect width="100" height="66.67" rx="1.5" fill="none" stroke="#001A4D" strokeWidth="1" />
    </g>
  );
}
